'use client'

import { LoaderCircle } from 'lucide-react'
import * as React from 'react'
import { createContext, useContext } from 'react'

import { cn } from '~/lib/utils'

// Types
interface StepperContextValue {
  activeStep: number
  setActiveStep: (step: number) => void
  orientation: 'horizontal' | 'vertical'
}

interface StepItemContextValue {
  step: number
  state: StepState
  isDisabled: boolean
  isLoading: boolean
}

type StepState = 'active' | 'completed' | 'inactive' | 'loading'

// Contexts
const StepperContext = createContext<StepperContextValue | undefined>(undefined)
const StepItemContext = createContext<StepItemContextValue | undefined>(
  undefined,
)

function useStepper() {
  const context = useContext(StepperContext)
  if (!context) {
    throw new Error('useStepper must be used within a Stepper')
  }
  return context
}

function useStepItem() {
  const context = useContext(StepItemContext)
  if (!context) {
    throw new Error('useStepItem must be used within a StepperItem')
  }
  return context
}

// Components
interface StepperProps extends React.HTMLAttributes<HTMLDivElement> {
  defaultValue?: number
  value?: number
  onValueChange?: (value: number) => void
  orientation?: 'horizontal' | 'vertical'
}

const Stepper = React.forwardRef<HTMLDivElement, StepperProps>(
  (
    {
      defaultValue = 0,
      value,
      onValueChange,
      orientation = 'horizontal',
      className,
      ...props
    },
    ref,
  ) => {
    const [activeStep, setInternalStep] = React.useState(defaultValue)

    const setActiveStep = React.useCallback(
      (step: number) => {
        if (value === undefined) {
          setInternalStep(step)
        }
        onValueChange?.(step)
      },
      [value, onValueChange],
    )

    const currentStep = value ?? activeStep

    return (
      <StepperContext.Provider
        value={{
          activeStep: currentStep,
          setActiveStep,
          orientation,
        }}
      >
        <div
          ref={ref}
          className={cn(
            'group/stepper inline-flex data-[orientation=horizontal]:w-full data-[orientation=horizontal]:flex-row data-[orientation=vertical]:flex-col',
            className,
          )}
          data-orientation={orientation}
          {...props}
        />
      </StepperContext.Provider>
    )
  },
)
Stepper.displayName = 'Stepper'

// StepperItem
interface StepperItemProps extends React.HTMLAttributes<HTMLDivElement> {
  step: number
  completed?: boolean
  disabled?: boolean
  loading?: boolean
}

const StepperItem = React.forwardRef<HTMLDivElement, StepperItemProps>(
  (
    {
      step,
      completed = false,
      disabled = false,
      loading = false,
      className,
      children,
      ...props
    },
    ref,
  ) => {
    const { activeStep } = useStepper()

    const state: StepState
      = completed || step < activeStep
        ? 'completed'
        : activeStep === step
          ? 'active'
          : 'inactive'

    const isLoading = loading && step === activeStep

    return (
      <StepItemContext.Provider
        value={{ step, state, isDisabled: disabled, isLoading }}
      >
        <div
          ref={ref}
          className={cn(
            'group/step flex items-center group-data-[orientation=horizontal]/stepper:flex-row group-data-[orientation=vertical]/stepper:flex-col',
            className,
          )}
          data-state={state}
          {...(isLoading ? { 'data-loading': true } : {})}
          {...props}
        >
          {children}
        </div>
      </StepItemContext.Provider>
    )
  },
)
StepperItem.displayName = 'StepperItem'

// StepperTrigger
interface StepperTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean
}

const StepperTrigger = React.forwardRef<HTMLButtonElement, StepperTriggerProps>(
  ({ asChild = false, className, children, ...props }, ref) => {
    const { setActiveStep } = useStepper()
    const { state, step, isDisabled } = useStepItem()

    if (asChild) {
      return <div className={className}>{children}</div>
    }

    return (
      <button
        ref={ref}
        className={cn(
          'inline-flex items-center gap-3 disabled:pointer-events-none disabled:opacity-50',
          className,
          state === 'active' && 'gap-[6px]',
        )}
        onClick={() => setActiveStep(step)}
        disabled={isDisabled}
        {...props}
      >
        {children}
      </button>
    )
  },
)
StepperTrigger.displayName = 'StepperTrigger'

// StepperIndicator
interface StepperIndicatorProps extends React.HTMLAttributes<HTMLDivElement> {
  asChild?: boolean
}

export const HexagonShape: React.FC<{
  className?: string
  children: React.ReactNode
}> = ({ className, children }) => (
  <div
    className={cn(
      'relative flex items-center justify-center w-[34px] h-[39px] max-sm:w-[30px] max-sm:h-[34px]',
      className,
    )}
  >
    <svg
      width="34"
      height="39"
      viewBox="0 0 34 39"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="absolute inset-0 size-full"
    >
      <path
        d="M34 26.9571L34 12.0429C34 10.4032 33.1481 8.89183 31.7767 8.07198L19.2267 0.614889C17.8484 -0.204963 16.1516 -0.204963 14.7733 0.614889L2.22326 8.07197C0.844976 8.89183 -3.17093e-07 10.4032 -4.6044e-07 12.0429L-1.76428e-06 26.9571C-1.90763e-06 28.5968 0.8519 30.1082 2.22326 30.928L14.7733 38.3851C16.1516 39.205 17.8484 39.205 19.2267 38.3851L31.7767 30.928C33.155 30.1082 34 28.5968 34 26.9571Z"
        fill="#FFD659"
      />
    </svg>
    <div className="relative z-10 text-[21px] font-bold max-sm:text-[18px]">
      {children}
    </div>
  </div>
)

// Arrow SVG Component
export const ArrowSeparator: React.FC<{ className?: string }> = ({
  className,
}) => (
  <svg
    width="131"
    height="12"
    viewBox="0 0 131 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={cn('w-full max-w-[131px] h-3', className)}
    preserveAspectRatio="xMidYMid meet"
  >
    <path
      d="M1 5.25C0.585786 5.25 0.25 5.58579 0.25 6C0.25 6.41421 0.585786 6.75 1 6.75V5.25ZM130.53 6.53033C130.823 6.23744 130.823 5.76256 130.53 5.46967L125.757 0.696699C125.464 0.403806 124.99 0.403806 124.697 0.696699C124.404 0.989592 124.404 1.46447 124.697 1.75736L128.939 6L124.697 10.2426C124.404 10.5355 124.404 11.0104 124.697 11.3033C124.99 11.5962 125.464 11.5962 125.757 11.3033L130.53 6.53033ZM1 6V6.75H130V6V5.25H1V6Z"
      fill="#BEAD9C"
    />
  </svg>
)

const StepperIndicator = React.forwardRef<
  HTMLDivElement,
  StepperIndicatorProps
>(({ asChild = false, className, children, ...props }, ref) => {
  const { state, step, isLoading } = useStepItem()

  // Use HexagonShape for the first step (step 0) when active
  if (state === 'active') {
    return <HexagonShape className={className}>{step + 1}</HexagonShape>
  }

  return (
    <div
      ref={ref}
      className={cn(
        'relative flex size-[34px] shrink-0 items-center justify-center rounded-full bg-[#ebe5dc] text-[21px] font-bold text-black max-sm:size-[30px] max-sm:text-[18px]',
        className,
      )}
      data-state={state}
      {...props}
    >
      {asChild ? (
        children
      ) : (
        <>
          <span className="transition-all group-data-[loading=true]/step:scale-0 group-data-[state=completed]/step:scale-1 group-data-[loading=true]/step:opacity-0 group-data-[state=completed]/step:opacity-1 group-data-[loading=true]/step:transition-none">
            {step + 1}
          </span>
          {isLoading && (
            <span className="absolute transition-all">
              <LoaderCircle
                className="animate-spin"
                size={14}
                strokeWidth={2}
                aria-hidden="true"
              />
            </span>
          )}
        </>
      )}
    </div>
  )
})
StepperIndicator.displayName = 'StepperIndicator'

// StepperTitle
const StepperTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn(
      'text-[14px] font-normal text-[#bead9c] whitespace-nowrap max-sm:text-[12px]',
      className,
    )}
    {...props}
  />
))
StepperTitle.displayName = 'StepperTitle'

// StepperDescription
const StepperDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
))
StepperDescription.displayName = 'StepperDescription'

// StepperSeparator
const StepperSeparator = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  // Use ArrowSeparator for horizontal orientation
  const orientation = useContext(StepperContext)?.orientation

  if (orientation === 'horizontal') {
    return (
      <div
        className={cn('flex items-center justify-center h-[39px]', className)}
        ref={ref}
        {...props}
      >
        <ArrowSeparator className="shrink-0" />
      </div>
    )
  }

  // Default separator for vertical orientation
  return (
    <div
      ref={ref}
      className={cn(
        'm-0.5 bg-muted group-data-[orientation=horizontal]/stepper:h-0.5 group-data-[orientation=vertical]/stepper:h-12 group-data-[orientation=horizontal]/stepper:w-full group-data-[orientation=vertical]/stepper:w-0.5 group-data-[orientation=horizontal]/stepper:flex-1 group-data-[state=completed]/step:bg-primary',
        className,
      )}
      {...props}
    />
  )
})
StepperSeparator.displayName = 'StepperSeparator'

export {
  Stepper,
  StepperDescription,
  StepperIndicator,
  StepperItem,
  StepperSeparator,
  StepperTitle,
  StepperTrigger,
}
